import { FC, useMemo, useState } from 'react'
import { Button, Input } from 'antd'
import { ToolCallItem, parseArgs, prettyJson } from './timeline'
import { Tool, ToolContent, ToolHeader } from '@/components/ai-elements/tool'
import { ToolCallCard, LOCATION_STYLE, formatDuration } from './ToolCallCard'
import { cn } from '@/lib/utils'

/** doc 118:question 工具 — AI 向用户推送选择题(单选/多选 + 每题自定义回复)。
 *  交互态(QuestionCard):pending 时渲染答题卡,提交后走现有 frontend 工具回路续传 LLM。
 *  摘要态(QuestionSummaryCard):success(含历史加载)渲染 问题→所选 紧凑视图。
 */

export const QUESTION_TOOL_NAME = 'question'

export interface QuestionOption {
  label: string
  description?: string
}

export interface QuestionSpec {
  question: string
  header?: string
  multiple?: boolean
  options?: QuestionOption[]
}

export interface QuestionAnswer {
  question: string
  header?: string
  selected: string[]   // 选中的 option label;纯自定义回复时为 []
  custom?: string      // 自定义回复
}

export interface QuestionAnswersPayload {
  answers: QuestionAnswer[]
}

// ── 解析(args/result 可能是对象,也可能是 JSON 字符串 — 历史从 DB 加载是字符串) ──

function parseMaybeJson(v: unknown): unknown {
  if (typeof v !== 'string') return v
  try {
    return JSON.parse(v)
  } catch {
    return v
  }
}

function parseQuestions(args: unknown): QuestionSpec[] {
  const qs = (parseMaybeJson(args) as any)?.questions
  return Array.isArray(qs)
    ? qs.filter((q: any) => q && typeof q.question === 'string' && Array.isArray(q.options))
    : []
}

function parseAnswers(result: unknown): QuestionAnswer[] | null {
  const ans = (parseMaybeJson(result) as any)?.answers
  return Array.isArray(ans) ? ans : null
}

function frontendPill() {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full border px-1.5 text-[10px] font-medium leading-4',
        LOCATION_STYLE.frontend,
      )}
    >
      frontend
    </span>
  )
}

// ── 交互态:答题卡 ──

export const QuestionCard: FC<{
  item: ToolCallItem
  onSubmit: (payload: QuestionAnswersPayload) => void
}> = ({ item, onSubmit }) => {
  const questions = useMemo(() => parseQuestions(item.args), [item.args])

  const [selections, setSelections] = useState<string[][]>(() => questions.map(() => []))
  const [customs, setCustoms] = useState<string[]>(() => questions.map(() => ''))

  // 参数不合法(无 questions/options)→ 只读展示原始参数,不让用户答题
  if (questions.length === 0) {
    return (
      <Tool open onOpenChange={() => {}}>
        <ToolHeader
          title={QUESTION_TOOL_NAME}
          state="pending"
          preview="参数缺少 questions,无法渲染答题卡"
          extra={frontendPill()}
        />
        <ToolContent>
          <pre className="overflow-x-auto rounded-md bg-muted/50 p-2 text-xs whitespace-pre-wrap break-all">
            {prettyJson(item.args)}
          </pre>
        </ToolContent>
      </Tool>
    )
  }

  const answered = (i: number) => selections[i].length > 0 || customs[i].trim() !== ''
  const allAnswered = questions.every((_, i) => answered(i))
  const answeredCount = questions.reduce((n, _, i) => n + (answered(i) ? 1 : 0), 0)

  // doc 118:单选互斥 — 选选项清空该题自定义;输入自定义取消该题选项。多选时共存。
  const toggleOption = (i: number, label: string, multiple?: boolean) => {
    setSelections(prev => prev.map((sel, idx) => {
      if (idx !== i) return sel
      if (multiple) return sel.includes(label) ? sel.filter(s => s !== label) : [...sel, label]
      return sel.includes(label) ? [] : [label] // 单选再点同一项 = 取消
    }))
    if (!multiple) setCustoms(prev => prev.map((c, idx) => (idx === i ? '' : c)))
  }

  const setCustom = (i: number, value: string, multiple?: boolean) => {
    setCustoms(prev => prev.map((c, idx) => (idx === i ? value : c)))
    if (value.trim() !== '' && !multiple) {
      setSelections(prev => prev.map((sel, idx) => (idx === i ? [] : sel)))
    }
  }

  const submit = () => {
    if (!allAnswered) return
    onSubmit({
      answers: questions.map((q, i) => ({
        question: q.question,
        header: q.header,
        selected: selections[i],
        custom: customs[i].trim() || undefined,
      })),
    })
  }

  return (
    <Tool open onOpenChange={() => {}}>
      <ToolHeader
        title={QUESTION_TOOL_NAME}
        state="pending"
        preview={`等待回答 ${answeredCount}/${questions.length}`}
        extra={frontendPill()}
      />
      <ToolContent>
        <div className="flex flex-col gap-3">
          {questions.map((q, i) => (
            <div key={i} className="rounded-md border bg-muted/20 p-3">
              <div className="mb-2 flex items-baseline gap-2">
                <span className="min-w-0 text-sm font-medium">
                  {q.header && <span className="text-xs text-muted-foreground">{q.header} · </span>}
                  {i + 1}. {q.question}
                </span>
                {q.multiple && (
                  <span className="shrink-0 rounded bg-muted px-1 text-[10px] text-muted-foreground">多选</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                {(q.options || []).map(opt => {
                  const checked = selections[i].includes(opt.label)
                  return (
                    <label
                      key={opt.label}
                      className={cn(
                        'flex cursor-pointer items-start gap-2 rounded-md border px-2.5 py-2 transition-colors hover:bg-muted/40',
                        checked && 'border-primary/40 bg-primary/5',
                      )}
                    >
                      <input
                        type={q.multiple ? 'checkbox' : 'radio'}
                        className="mt-0.5 accent-primary"
                        checked={checked}
                        onChange={() => toggleOption(i, opt.label, q.multiple)}
                      />
                      <span className="min-w-0">
                        <span className="block text-sm">{opt.label}</span>
                        {opt.description && (
                          <span className="block text-xs text-muted-foreground">{opt.description}</span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
              <Input
                size="small"
                allowClear
                className="mt-2"
                placeholder="自定义回复(可不选选项直接输入)"
                value={customs[i]}
                onChange={e => setCustom(i, e.target.value, q.multiple)}
              />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Button size="small" type="primary" disabled={!allAnswered} onClick={submit}>
              提交回答
            </Button>
            {!allAnswered && (
              <span className="text-xs text-muted-foreground">每题需选择一项或填写自定义回复</span>
            )}
          </div>
        </div>
      </ToolContent>
    </Tool>
  )
}

// ── 摘要态:已回答(含历史) ──

export const QuestionSummaryCard: FC<{ item: ToolCallItem }> = ({ item }) => {
  const [open, setOpen] = useState(false)
  const questions = useMemo(() => parseQuestions(item.args), [item.args])
  const answers = useMemo(() => parseAnswers(item.result), [item.result])

  // 结果/参数解析失败 → 回退通用工具卡片(JSON 视图)
  if (questions.length === 0 || !answers) {
    return <ToolCallCard item={item} />
  }

  const answeredCount = questions.reduce((n, _q, i) => {
    const a = answers[i]
    const has = a && ((a.selected?.length ?? 0) > 0 || (a.custom || '').trim() !== '')
    return n + (has ? 1 : 0)
  }, 0)

  return (
    <Tool open={open} onOpenChange={setOpen}>
      <ToolHeader
        title={QUESTION_TOOL_NAME}
        state="success"
        preview={`已回答 ${answeredCount}/${questions.length}`}
        duration={item.durationMs != null ? formatDuration(item.durationMs) : undefined}
        extra={frontendPill()}
      />
      <ToolContent>
        <div className="flex flex-col gap-2">
          {questions.map((q, i) => {
            const a = answers[i]
            const selected = a?.selected || []
            const custom = (a?.custom || '').trim()
            return (
              <div key={i} className="rounded-md border p-2.5">
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                  {i + 1}. {q.header ? `${q.header} · ` : ''}{q.question}
                </div>
                <div className="flex flex-wrap gap-1">
                  {selected.map(s => (
                    <span key={s} className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">{s}</span>
                  ))}
                  {custom && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs">"{custom}"</span>
                  )}
                  {selected.length === 0 && !custom && (
                    <span className="text-xs text-muted-foreground">未回答</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </ToolContent>
    </Tool>
  )
}